import React, { useCallback } from 'react';
import styled from 'styled-components';
import { createSelector } from '@reduxjs/toolkit';

import type { ISortedAvailableShift } from '@/types/schedule';
import type { Hue } from '@/types/styles';

import { CrossIcon } from '@/assets/icons';
import { colors } from '@/styles/colors';
import { flex } from '@/styles/theme';
import { formatDate } from '@/helpers/date';
import { formatStartEndTimeWithTimezone, getScheduleShortName } from '@/helpers';
import { useAppSelector, useTimezone } from '@/hooks';
import { ColoredEllipse } from '@/components/common';
import { DEFAULT_CONTRACT_COLOR } from '@/config/variables';
import { selectScheduleColors, selectSchedulesList } from '@/store/selectors/scheduleSelectors';
import { getBlockSettings, getDateFromBlock } from '@/helpers/block';

interface IProps {
  shiftsByDay: Record<string, ISortedAvailableShift[]>;
  index: number;
  onRemoveShift: (shift: ISortedAvailableShift) => void;
}

const pickedShiftsScheduleItemSelector = createSelector(
  selectSchedulesList,
  selectScheduleColors,
  (schedulesList, scheduleColors) => ({ schedulesList, scheduleColors }),
);

const PickedShiftsScheduleItem = ({ shiftsByDay, index, onRemoveShift }: IProps): React.ReactElement => {
  const { scheduleColors, schedulesList } = useAppSelector(pickedShiftsScheduleItemSelector);
  const { timezoneName } = useTimezone();
  const firstShiftFromThisSchedule = shiftsByDay[Object.keys(shiftsByDay)[0]][0];

  const formatShiftLabel = useCallback(
    (startBlock: number, endBlock: number) => {
      const position = getBlockSettings(startBlock, endBlock, timezoneName);
      return formatStartEndTimeWithTimezone(position);
    },
    [timezoneName],
  );

  return (
    <Container>
      <ScheduleShortName blockColor={scheduleColors?.[firstShiftFromThisSchedule.scheduleId]}>
        {getScheduleShortName(schedulesList, firstShiftFromThisSchedule.scheduleId)}
      </ScheduleShortName>
      <ScheduleName>{firstShiftFromThisSchedule.scheduleName}</ScheduleName>
      <ScheduleClientName>
        <ColoredEllipse lineColor={scheduleColors?.[firstShiftFromThisSchedule.scheduleId]} />
        {index + 1}
        {firstShiftFromThisSchedule.clientName}
      </ScheduleClientName>

      {Object.entries(shiftsByDay).map(([key, shiftsByDay]) => (
        <React.Fragment key={key}>
          <WeekLabel>{formatDate(getDateFromBlock(shiftsByDay[0].startBlock, timezoneName), 'EEE d')}</WeekLabel>
          {shiftsByDay.map(shift => (
            <ShiftRow key={`shift_row_${shift.shiftId}`}>
              {formatShiftLabel(shift.startBlock, shift.endBlock)}
              <ButtonDeleteShiftRow onClick={() => onRemoveShift(shift)}>
                <IconDeleteShiftRow src={CrossIcon} alt="Remove" />
              </ButtonDeleteShiftRow>
            </ShiftRow>
          ))}
        </React.Fragment>
      ))}
    </Container>
  );
};

export { PickedShiftsScheduleItem };

const Container = styled.div.attrs({
  className: 'ps-3 py-3 pe-1 my-2',
})`
  width: 100%;

  background-color: #fff;

  border: 1px solid #e6f3fc;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.075);
`;

const ScheduleShortName = styled.div<{ blockColor?: Hue }>`
  display: flex;

  width: 100%;
  margin-bottom: 4px;

  color: ${({ blockColor }) => colors[blockColor || DEFAULT_CONTRACT_COLOR].c300};
  font-weight: 700;
  font-size: 10px;
  line-height: 12px;
`;

const ScheduleName = styled.div`
  margin-top: 3px;
  margin-bottom: 3px;
  height: 20px;
  width: 100%;

  font-size: 12px;
  line-height: 18px;
  color: #5a5a5a;

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const ScheduleClientName = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: #4b5563;

  margin-top: 3px;
  margin-bottom: 3px;
  height: 18px;
  width: 100%;

  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const WeekLabel = styled.div.attrs({
  className: 'pt-2 pb-1',
})`
  font-size: 12px;
  line-height: 18px;
  color: #5a5a5a;
`;

const ShiftRow = styled.div.attrs({
  className: `${flex.rowCenter} justify-content-between pe-2`,
})`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;

  color: #4b5563;
`;

const IconDeleteShiftRow = styled.img`
  width: 8px;
  height: 8px;
  margin-right: 4px;
`;

const ButtonDeleteShiftRow = styled.button`
  border: none;
  background: none;
`;
